import { useCallback } from 'react';
import type { SearchParamsWriteOptions } from '~utils/use-search-params';
import { useCouponList } from '../kpis/use-coupon-list';
import type { CouponListState, CouponOption } from '../kpis/use-coupon-list';

export type PageCouponQuery = {
    couponId?: string;
};

export interface PageCouponSearch {
    params: PageCouponQuery;
    setParams: (
        params: Partial<PageCouponQuery>,
        options?: SearchParamsWriteOptions
    ) => void;
}

export interface UsePageCouponOptions {
    search: PageCouponSearch;
    enabled?: boolean;
}

export interface PageCoupon {
    couponId: string | undefined;
    setCoupon: (id: string) => void;
    /** `[]` while loading, empty, or failed; check `state` to tell those apart. */
    options: CouponOption[];
    state: CouponListState;
}

/** `useSearchParams` parses a fully-numeric value as a number, but every
 * `CouponOption.id` is a string; anything else non-string/number is dropped. */
const coerceQueryCouponId = (raw: unknown): string | undefined => {
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'number') return String(raw);
    return undefined;
};

/** Page-level sibling of `usePageFilter`, for coupon selection: URL-backed,
 * so one selector can drive every coupon card on a page. Reuses
 * `useCouponList` for the fetch rather than duplicating it. */
export function usePageCoupon({
    search,
    enabled = true,
}: UsePageCouponOptions): PageCoupon {
    const { params, setParams } = search;
    const state = useCouponList(enabled);

    const couponId = coerceQueryCouponId(params.couponId);

    const setCoupon = useCallback(
        (id: string) => setParams({ couponId: id }, { preserveScroll: true }),
        [setParams]
    );

    const options = state.status === 'ready' ? state.coupons : [];

    return { couponId, setCoupon, options, state };
}
